import React, { useState, useMemo, useEffect } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Dialog from "@material-ui/core/Dialog";
import DialogContentText from "@material-ui/core/DialogContentText";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import Input from "@material-ui/core/Input";
import InputLabel from "@material-ui/core/InputLabel";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import {
  cashAppUser,
  paypalEmail,
  venmoUserName,
  zellePhoneOrEmail,
} from "../enums";
import { getQRCode } from "../ajax";

const useStyles = makeStyles((theme) => ({
  nextButton: {
    marginTop: theme.spacing(),
  },
}));

export default ({
  open,
  paymentMethod,
  updatePaymentMethod,
  paymentUser,
  updatePaymentUser,
  cardsToEvaluatePrice,
  cardsToNotEvaluatePrice,
  updatePaymentDiscount,
  currencyCaption,
  onClose,
}) => {
  const total = cardsToEvaluatePrice + cardsToNotEvaluatePrice;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles();
  const [qrCode, setQRCode] = useState("");

  const onFinish = useMemo(() => () => onClose(true), [onClose]);

  const methodComplete =
    !!paymentMethod && !!paymentUser && paymentUser.trim().length > 0;

  const getAmount = useMemo(
    () => (context) =>
      context.discount * cardsToEvaluatePrice + cardsToNotEvaluatePrice,
    [cardsToEvaluatePrice, cardsToNotEvaluatePrice]
  );

  const getDiscount = useMemo(
    () => (context) => total > 0 ? (total - context.amount) / total : 0,
    [total]
  );

  const paymentMethods = useMemo(
    () => ({
      Venmo: {
        label: "Username",
        username: venmoUserName,
        link: `https://venmo.com/u/${venmoUserName}`,
        discount: 0.95,
        get amount() {
          return getAmount(this);
        },
        get discountRatio() {
          return getDiscount(this);
        },
      },
      "Cash App": {
        label: "Username",
        username: cashAppUser,
        link: `https://cash.app/${cashAppUser}`,
        discount: 0.95,
        get amount() {
          return getAmount(this);
        },
        get discountRatio() {
          return getDiscount(this);
        },
      },
      Zelle: {
        label: "Phone or Email",
        username: zellePhoneOrEmail,
        link: `tel:${zellePhoneOrEmail}`,
        discount: 0.95,
        get amount() {
          return getAmount(this);
        },
        get discountRatio() {
          return getDiscount(this);
        },
      },
      PayPal: {
        label: "Email",
        link: `${window.location.protocol}//${
          window.location.host
        }/paypal/${encodeURIComponent(paypalEmail)}/${encodeURIComponent(
          total
        )}`,
        username: paypalEmail,
        discount: 1,
        get amount() {
          return getAmount(this);
        },
        get discountRatio() {
          return getDiscount(this);
        },
      },
    }),
    [getAmount, getDiscount, total]
  );

  const link = paymentMethod ? paymentMethods[paymentMethod].link : "";

  useEffect(() => {
    if (link) {
      getQRCode(link).then((code) => setQRCode(code));
    }
  }, [link]);

  useEffect(() => {
    if (paymentMethod && paymentMethods[paymentMethod]) {
      updatePaymentDiscount({
        target: {
          value: total > 0 ? paymentMethods[paymentMethod].amount / total : 0,
        },
      });
    }
  }, [paymentMethod, updatePaymentDiscount, paymentMethods, total]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth fullScreen={isMobile}>
      <DialogTitle>Complete Payment</DialogTitle>
      <DialogContent>
        <DialogContentText>
          NOTE: Your order is not complete until you hit "Submit Order"{" "}
        </DialogContentText>
        <RadioGroup
          name="method"
          value={paymentMethod}
          onChange={updatePaymentMethod}
        >
          {Object.keys(paymentMethods).map((option) => (
            <FormControlLabel
              key={option}
              label={`${option} - ${currencyCaption}${paymentMethods[
                option
              ].amount.toFixed(2)}${
                paymentMethods[option].amount !== total
                  ? ` ($${(total - paymentMethods[option].amount).toFixed(
                      2
                    )} discount)`
                  : ""
              }`}
              value={option}
              control={<Radio />}
            />
          ))}
        </RadioGroup>
        {paymentMethod ? (
          <>
            <FormControl fullWidth style={{ marginBottom: 8 }}>
              <InputLabel htmlFor="payment-user">
                Enter {paymentMethod} {paymentMethods[paymentMethod].label}
              </InputLabel>
              <Input
                id="payment-user"
                type="text"
                defaultValue={paymentUser}
                onInput={updatePaymentUser}
                maxLength={200}
                fullWidth
                required
                inputProps={{ autoComplete: "cc-csc" }}
              />
            </FormControl>
            <Typography component="div" style={{ textAlign: "center" }}>
              Please send {currencyCaption}
              <strong>
                {paymentMethods[paymentMethod].amount.toFixed(2)}
              </strong>{" "}
              via {paymentMethod} to{" "}
              <a
                href={paymentMethods[paymentMethod].link}
                target={
                  paymentMethods[paymentMethod].link.startsWith("http")
                    ? "_blank"
                    : undefined
                }
              >
                <strong>{paymentMethods[paymentMethod].username}</strong>
              </a>
              <div>
                {qrCode ? <img src={qrCode} style={{ maxWidth: 150 }} /> : null}
              </div>
            </Typography>
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button
          fullWidth
          className={classes.nextButton}
          color="primary"
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          className={classes.nextButton}
          color="primary"
          variant="outlined"
          onClick={onFinish}
          disabled={!methodComplete}
        >
          Submit Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};
